import {
    ApolloClient, ApolloLink, concat, createHttpLink, DefaultContext, InMemoryCache,
} from "@apollo/client";
import { GraphQLErrors } from "@apollo/client/errors";

import { onError } from "@apollo/client/link/error";
import { GraphQLError } from "graphql";

import { LoggingHandler } from "./contexts/logging/LoggingContext";

export const FOQAL_GRAPHQL_URL = process.env.REACT_APP_API_URL;

const link = createHttpLink({
    uri: FOQAL_GRAPHQL_URL,
    credentials: "include",
});
const WHITELISTED_PATHS = ["/login", "/answers", "/channel-invite"];

const UNAUTHORIZED_MESSAGE = "Not authorized to perform this action.";

type ErroHandler = () => void;
const authErrorRegistry: ErroHandler[] = [];

export function subscribeError(err: ErroHandler) {
    authErrorRegistry.push(err);
}

export function unsubscribeError(err: ErroHandler) {
    const index = authErrorRegistry.findIndex(err);
    authErrorRegistry.splice(index, 1);
}

export function hasLoginError(errors?: GraphQLError[] | GraphQLErrors) {
    return errors?.some(error => {
        const ext = error.extensions as {code: number, exception: {code: string}} | undefined;
        return error.message === UNAUTHORIZED_MESSAGE || ext?.code === 401 || ext?.exception?.code === "401";
    });
}

function handleLoginError(operationName: string, errors?: GraphQLError[] | GraphQLErrors) {
    const currentPath = window.location.pathname;

    if (hasLoginError(errors)
        && (WHITELISTED_PATHS.every(path => !currentPath?.startsWith(path) && currentPath !== "/"))) {
        authErrorRegistry.forEach(handler => handler());
        const params = new URLSearchParams();
        params.append("redirect", window.location.href);
        window.location.href = `/login?${params.toString()}`;
    }
}

const timeStartLink = new ApolloLink((operation, forward) => {
    operation.setContext({ startTime: performance.now() });
    return forward(operation);
});

const logLink = new ApolloLink((operation, forward) => forward(operation).map(response => {
    const context = operation.getContext();

    if (operation.operationName === "StoreLogs") {
        return response;
    }
    LoggingHandler.instance.debug({
        component: "API",
        requestId: context?.response?.headers?.get("x-req-id"),
        operationName: operation.operationName,
        responseStatus: context?.response.status,
        responseTime: performance.now() - context.startTime as number,
    }, "API request");

    return response;
}));

const errorLink = onError(err => {
    const { graphQLErrors, networkError, operation } = err;
    // const has401 = graphQLErrors?.find(err => err.extensions.)
    if (graphQLErrors) {
        const context = operation.getContext();
        // eslint-disable-next-line no-console
        LoggingHandler.instance.error({
            component: "API",
            requestId: context?.response?.headers?.get("x-req-id"),
            operationName: operation.operationName,
            responseStatus: context?.response.status,
            responseTime: performance.now() - context.startTime as number,
            errors: graphQLErrors.map(graphError => ({
                message: graphError.message,
                location: graphError.locations,
                path: graphError.path,
                innerError: graphError.originalError,
            })),
        }, "GraphQL Error");

        handleLoginError(operation.operationName, graphQLErrors);
    }

    if (networkError) {
        // eslint-disable-next-line no-console
        LoggingHandler.instance.error(networkError, { component: "API" }, "Network error occured");
    }
});

const client = new ApolloClient({
    link: concat(timeStartLink, concat(errorLink, concat(logLink, link))),
    cache: new InMemoryCache(),
});

export default client;

export function removeTypename<T extends {__typename?: string}>(obj: T): T {
    if (obj?.__typename) {
        // eslint-disable-next-line no-param-reassign
        delete obj.__typename;
    }
    Object.values(obj).forEach(value => {
        if (value) {
            if (Array.isArray(value)) {
                value.forEach(row => {
                    if (typeof row === "object") {
                        removeTypename(row);
                    }
                });
            } else if (typeof value === "object") {
                removeTypename(value);
            }
        }
    });
    return obj;
}

export function removeTypenameList<T extends {__typename?: string}>(obj: T[]): T[] {
    return obj.map(row => removeTypename({ ...row }));
}
